import { useEffect, useState, type ReactNode } from 'react';
import type { ApiConnection } from '@jfh/intake-shared';
import { api } from '../../lib/api.js';
import { API_ORIGIN } from '../../lib/deployment.js';
import { useToast } from '../../components/Toast.js';
import { Icon } from '../../components/Icon.js';
import {
  CopyRow,
  EmailConnectionPanel,
  FormsUrlPanel,
  IqAlertEmailsPanel,
  IqDerivationsPanel,
  WixWebhookPanel,
} from './panels.js';
import { plainIqResult } from './logic.js';

const CALLBACK_URL = `${API_ORIGIN}/api/webhooks/power-automate`;

type Status = { tone: 'approved' | 'pending' | 'neutral'; text: string } | null;

/**
 * System set-up → Connections (F9 P5): one row per outside system, with a
 * status badge, opening to its settings. iQ is a guided four-step set-up.
 */
export function Connections() {
  const [iq, setIq] = useState<Status>(null);
  const [wix, setWix] = useState<Status>(null);
  const [email, setEmail] = useState<Status>(null);
  const [alerts, setAlerts] = useState<Status>(null);

  useEffect(() => {
    api
      .getApiConnection()
      .then((c) =>
        setIq(c.baseUrl && c.bearerTokenSet ? { tone: 'approved', text: 'Set up' } : { tone: 'pending', text: 'Not set up' }),
      )
      .catch(() => undefined);
    api
      .getWixWebhook()
      .then((r) => setWix(r.secret ? { tone: 'approved', text: 'Set up' } : { tone: 'neutral', text: 'Not set up' }))
      .catch(() => undefined);
    api
      .getEmailConnection()
      .then((c) => setEmail(c.apiKeySet ? { tone: 'approved', text: 'Set up' } : { tone: 'pending', text: 'Not set up' }))
      .catch(() => undefined);
    api
      .getIqAlertEmails()
      .then((r) =>
        setAlerts(
          r.emails.length
            ? { tone: 'approved', text: `${r.emails.length} ${r.emails.length === 1 ? 'person' : 'people'}` }
            : { tone: 'neutral', text: 'Nobody' },
        ),
      )
      .catch(() => undefined);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p className="field-help" style={{ margin: '0 0 4px' }}>
        The outside systems JFH Forms talks to. Each one is set up once; forms then use it.
      </p>
      <ConnRow
        icon="db"
        title="IntactIQ (iQ)"
        desc="Where approved applications become customers, suppliers and leads, and where iQ lists come from."
        status={iq}
        defaultOpen
      >
        <IqGuide onSaved={(ok) => setIq(ok ? { tone: 'approved', text: 'Set up' } : { tone: 'pending', text: 'Not set up' })} />
        <div className="divider" style={{ margin: '18px 0 12px' }} />
        <strong style={{ fontSize: 13.5 }}>Values worked out for every form</strong>
        <IqDerivationsPanel bare />
      </ConnRow>
      <ConnRow icon="teams" title="Teams approvals" desc="How Power Automate flows send an approval decision back.">
        <p className="field-help" style={{ marginTop: 0 }}>
          Each flow posts an approval card. When someone approves or declines, the flow sends the decision to this
          address, with the header <span className="mono">X-Webhook-Secret</span> set to the Worker secret{' '}
          <span className="mono">POWER_AUTOMATE_HMAC_SECRET</span>. (Channels using the JFH Forms Teams app don’t need
          this.)
        </p>
        <CopyRow text={CALLBACK_URL} />
        <p className="field-help" style={{ marginTop: 8 }}>
          Body:{' '}
          <span className="mono">{'{ submissionId, decision: "approve"|"decline", actor, reason?, internalData? }'}</span>
        </p>
      </ConnRow>
      <ConnRow icon="link" title="Wix website forms" desc="Lets forms filled in on the Wix site come in here." status={wix}>
        <WixWebhookPanel bare />
      </ConnRow>
      <ConnRow icon="mail" title="Sending emails (Resend)" desc="Emails to applicants and iQ failure alerts." status={email}>
        <EmailConnectionPanel bare />
      </ConnRow>
      <ConnRow icon="external" title="Public forms address" desc="Where applicants open the forms. Used in embed codes.">
        <FormsUrlPanel bare />
      </ConnRow>
      <ConnRow icon="flag" title="iQ failure alerts" desc="Who gets an email when an application doesn’t reach iQ." status={alerts}>
        <IqAlertEmailsPanel bare />
      </ConnRow>
    </div>
  );
}

function ConnRow({
  icon,
  title,
  desc,
  status,
  defaultOpen,
  children,
}: {
  icon: string;
  title: string;
  desc: string;
  status?: Status;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 18px',
          border: 'none',
          background: 'transparent',
          textAlign: 'left',
          color: 'var(--text-1)',
        }}
      >
        <span style={{ color: 'var(--text-3)', display: 'inline-flex' }}>
          <Icon name={icon} size={18} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <strong style={{ fontSize: 14.5 }}>{title}</strong>
          <br />
          <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{desc}</span>
        </span>
        {status && <span className={`badge ${status.tone}`}>{status.text}</span>}
        <span
          style={{ transform: `rotate(${open ? 180 : 0}deg)`, transition: 'transform .14s', color: 'var(--text-3)', display: 'inline-flex' }}
        >
          <Icon name="chevron" size={16} />
        </span>
      </button>
      {open && <div style={{ padding: '0 18px 18px 48px' }}>{children}</div>}
    </div>
  );
}

function Step({ n, title, hint, children }: { n: number; title: string; hint?: string; children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: 10, marginTop: 16 }}>
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: 99,
          background: 'var(--accent-soft)',
          color: 'var(--accent-text)',
          fontWeight: 800,
          fontSize: 12,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {n}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 13.5 }}>{title}</div>
        {hint && <div className="field-help" style={{ marginTop: 2 }}>{hint}</div>}
        <div style={{ marginTop: 8 }}>{children}</div>
      </div>
    </div>
  );
}

/** The iQ connection as four steps: where, sign-in, where records go, test. */
function IqGuide({ onSaved }: { onSaved: (ok: boolean) => void }) {
  const toast = useToast();
  const [baseUrl, setBaseUrl] = useState('');
  const [customerEndpoint, setCustomerEndpoint] = useState('');
  const [supplierEndpoint, setSupplierEndpoint] = useState('');
  const [leadEndpoint, setLeadEndpoint] = useState('');
  const [token, setToken] = useState('');
  const [tokenSet, setTokenSet] = useState(false);
  const [behindAccess, setBehindAccess] = useState(false);
  const [cfId, setCfId] = useState('');
  const [cfSecret, setCfSecret] = useState('');
  const [cfSecretSet, setCfSecretSet] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<{ r: Awaited<ReturnType<typeof api.testIq>>; ms: number } | null>(null);
  const [technical, setTechnical] = useState(false);

  useEffect(() => {
    api
      .getApiConnection()
      .then((c: ApiConnection) => {
        setBaseUrl(c.baseUrl ?? '');
        setCustomerEndpoint(c.customerEndpoint ?? '');
        setSupplierEndpoint(c.supplierEndpoint ?? '');
        setLeadEndpoint(c.leadEndpoint ?? '');
        setTokenSet(Boolean(c.bearerTokenSet));
        setCfId(c.cfAccessClientId ?? '');
        setCfSecretSet(Boolean(c.cfAccessClientSecretSet));
        setBehindAccess(Boolean(c.cfAccessClientId));
      })
      .catch(() => undefined)
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.putApiConnection({
        baseUrl,
        bearerToken: token || undefined,
        // Turning Access off clears the client id; the stored secret is then unused.
        cfAccessClientId: behindAccess ? cfId : '',
        cfAccessClientSecret: behindAccess ? cfSecret || undefined : undefined,
        customerEndpoint,
        supplierEndpoint,
        leadEndpoint,
      });
      const nowTokenSet = tokenSet || Boolean(token);
      setTokenSet(nowTokenSet);
      if (cfSecret) setCfSecretSet(true);
      setToken('');
      setCfSecret('');
      onSaved(Boolean(baseUrl) && nowTokenSet);
      toast({ title: 'iQ connection saved', body: 'Test it in step 4.', icon: 'check' });
    } catch (e) {
      toast({ title: 'Save failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setResult(null);
    const t0 = performance.now();
    try {
      const r = await api.testIq({ method: 'GET', entity: 'customer' });
      setResult({ r, ms: performance.now() - t0 });
    } catch (e) {
      toast({ title: 'Test failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setTesting(false);
    }
  };

  if (!loaded) return <div style={{ color: 'var(--text-3)' }}>Loading…</div>;
  const plain = result ? plainIqResult(result.r, result.ms) : null;

  return (
    <div>
      <Step n={1} title="Where is iQ?" hint="The address of iQ’s API, from IntactIQ.">
        <input
          className="input"
          placeholder="https://api.example.com/v2"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          aria-label="iQ address"
        />
      </Step>

      <Step n={2} title="How do we sign in?" hint="iQ gives you an API token. It’s stored safely and never shown again.">
        <input
          className="input"
          type="password"
          placeholder={tokenSet ? '•••••• (saved — leave blank to keep it)' : 'Paste the iQ token'}
          value={token}
          onChange={(e) => setToken(e.target.value)}
          aria-label="iQ token"
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, fontWeight: 600 }}>
          <input type="checkbox" checked={behindAccess} onChange={(e) => setBehindAccess(e.target.checked)} />
          iQ sits behind Cloudflare Access
        </label>
        {behindAccess && (
          <div style={{ display: 'grid', gap: 8, marginTop: 8, paddingLeft: 24 }}>
            <div className="field-help" style={{ marginTop: 0 }}>
              The service token from Cloudflare Zero Trust, sent as{' '}
              <span className="mono">CF-Access-Client-Id</span> / <span className="mono">CF-Access-Client-Secret</span>.
            </div>
            <input
              className="input mono"
              placeholder="Client ID (ends in .access)"
              value={cfId}
              onChange={(e) => setCfId(e.target.value)}
              aria-label="Cloudflare Access client ID"
            />
            <input
              className="input"
              type="password"
              placeholder={cfSecretSet ? '•••••• (saved — leave blank to keep it)' : 'Client secret'}
              value={cfSecret}
              onChange={(e) => setCfSecret(e.target.value)}
              aria-label="Cloudflare Access client secret"
            />
          </div>
        )}
      </Step>

      <Step n={3} title="Where do new records go?" hint="Usually leave these as they are.">
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 10px', alignItems: 'center', fontSize: 13 }}>
          <span>Customers</span>
          <input
            className="input mono"
            placeholder="/customers"
            value={customerEndpoint}
            onChange={(e) => setCustomerEndpoint(e.target.value)}
            aria-label="Customer endpoint"
          />
          <span>Suppliers</span>
          <input
            className="input mono"
            placeholder="/supplier"
            value={supplierEndpoint}
            onChange={(e) => setSupplierEndpoint(e.target.value)}
            aria-label="Supplier endpoint"
          />
          <span>Leads</span>
          <input
            className="input mono"
            placeholder="/lead"
            value={leadEndpoint}
            onChange={(e) => setLeadEndpoint(e.target.value)}
            aria-label="Lead endpoint"
          />
        </div>
        <div style={{ marginTop: 12 }}>
          <button className="btn primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Step>

      <Step n={4} title="Test it" hint="Asks iQ for customers with what’s saved. Nothing is created.">
        <button className="btn" onClick={() => void test()} disabled={testing}>
          <Icon name="send" size={14} /> {testing ? 'Testing…' : 'Test the connection'}
        </button>
        {plain && result && (
          <div
            role="status"
            style={{
              marginTop: 10,
              padding: '10px 12px',
              borderRadius: 'var(--r-md)',
              background: plain.ok ? 'var(--st-approved-soft)' : 'var(--st-declined-soft)',
              color: plain.ok ? 'var(--st-approved)' : 'var(--st-declined)',
              fontSize: 13,
              fontWeight: 600,
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
            }}
          >
            <Icon name={plain.ok ? 'check' : 'x'} size={15} style={{ flex: 'none', marginTop: 1 }} />
            <span>{plain.text}</span>
          </div>
        )}
        {result && (
          <div style={{ marginTop: 8 }}>
            <button className="btn ghost sm" onClick={() => setTechnical((t) => !t)} aria-expanded={technical}>
              {technical ? 'Hide technical details' : 'Show technical details'}
            </button>
            {technical && (
              <div
                style={{
                  marginTop: 6,
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--bg-sunken)',
                  fontSize: 12,
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  {result.r.error
                    ? `No response — ${result.r.gotHeaders ? 'connected, body read failed' : 'never reached / blocked'}`
                    : `HTTP ${result.r.status}`}
                </div>
                <div className="mono" style={{ color: 'var(--text-2)', wordBreak: 'break-all' }}>
                  {result.r.method} {result.r.url}
                </div>
                {result.r.error && (
                  <div className="mono" style={{ color: 'var(--st-declined)', marginTop: 4 }}>
                    {result.r.error}
                  </div>
                )}
                {Object.entries(result.r.headers)
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k} className="mono" style={{ color: 'var(--text-3)', marginTop: 2 }}>
                      {k}: {v}
                    </div>
                  ))}
                {result.r.body && (
                  <pre style={{ marginTop: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 160, overflow: 'auto' }}>
                    {result.r.body}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </Step>
    </div>
  );
}
